import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { integrations } from '@/data/integrations';
import { useScrollReveal } from '@/lib/useScrollReveal';

const frameworkLogos: Record<string, React.ReactNode> = {
  'LangChain': (
    <svg viewBox="0 0 40 40" className="w-10 h-10">
      <rect width="40" height="40" rx="8" fill="#1C1C1C" />
      <path d="M12 20L20 12L28 20L20 28L12 20Z" fill="#40C4FF" />
      <path d="M12 14L20 6L28 14" stroke="#40C4FF" strokeWidth="2" fill="none" />
      <path d="M12 26L20 34L28 26" stroke="#40C4FF" strokeWidth="2" fill="none" />
    </svg>
  ),
  'CrewAI': (
    <svg viewBox="0 0 40 40" className="w-10 h-10">
      <rect width="40" height="40" rx="8" fill="#1A1A2E" />
      <circle cx="20" cy="15" r="6" fill="#9D4EDD" />
      <circle cx="12" cy="28" r="5" fill="#E040FB" />
      <circle cx="28" cy="28" r="5" fill="#E040FB" />
      <line x1="20" y1="21" x2="12" y2="23" stroke="#9D4EDD" strokeWidth="2" />
      <line x1="20" y1="21" x2="28" y2="23" stroke="#9D4EDD" strokeWidth="2" />
    </svg>
  ),
  'AutoGen': (
    <svg viewBox="0 0 40 40" className="w-10 h-10">
      <rect width="40" height="40" rx="8" fill="#0078D4" />
      <rect x="10" y="12" width="8" height="16" rx="2" fill="white" />
      <rect x="22" y="12" width="8" height="16" rx="2" fill="white" />
      <path d="M18 20H22" stroke="#0078D4" strokeWidth="2" />
    </svg>
  ),
  'Custom REST': (
    <svg viewBox="0 0 40 40" className="w-10 h-10">
      <rect width="40" height="40" rx="8" fill="#FF6B35" />
      <path d="M20 10L30 20L20 30L10 20L20 10Z" fill="none" stroke="white" strokeWidth="2" />
      <circle cx="20" cy="20" r="4" fill="white" />
    </svg>
  ),
  'MCP Protocol': (
    <svg viewBox="0 0 40 40" className="w-10 h-10">
      <rect width="40" height="40" rx="8" fill="#1E3A5F" />
      <circle cx="20" cy="20" r="10" fill="none" stroke="#4ECDC4" strokeWidth="2" />
      <circle cx="20" cy="20" r="4" fill="#4ECDC4" />
      <line x1="20" y1="10" x2="20" y2="16" stroke="#4ECDC4" strokeWidth="2" />
      <line x1="20" y1="24" x2="20" y2="30" stroke="#4ECDC4" strokeWidth="2" />
      <line x1="10" y1="20" x2="16" y2="20" stroke="#4ECDC4" strokeWidth="2" />
      <line x1="24" y1="20" x2="30" y2="20" stroke="#4ECDC4" strokeWidth="2" />
    </svg>
  ),
};

export const Integrations: React.FC = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-20 bg-white" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-16 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            Works With Your Stack
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            First-class adapters for the frameworks you already use
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {integrations.map((integration, index) => (
            <Card
              key={integration.name}
              variant="interactive"
              className={`transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${(index + 1) * 100}ms` } as React.CSSProperties}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-shrink-0">
                    {frameworkLogos[integration.name] || (
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <span className="text-primary font-bold">{integration.name[0]}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">{integration.name}</h3>
                    <code className="text-xs text-text-muted font-mono">
                      {integration.package}
                    </code>
                  </div>
                </div>

                <p className="text-text-secondary text-sm mb-4 flex-grow">
                  {integration.description}
                </p>

                <a
                  href={integration.href}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  View Docs
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Integrations;